(() => {
  const CONFETTI_COLORS = ['var(--cls-accent)', '#ffffff', '#f5f5f5', '#e5e5e5', '#a3a3a3', 'var(--cls-accent)'];

  const track = (event) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event });
  };

  // Pass the landing page's query parameters (UTMs) on to the offer link, as the other listicles do.
  const passTrackingParameters = (story) => {
    const parameters = new URLSearchParams(window.location.search);
    if (!parameters.size) return;
    story.querySelectorAll('[data-story-cta]').forEach((link) => {
      try {
        const destination = new URL(link.href, window.location.origin);
        parameters.forEach((value, key) => {
          if (!destination.searchParams.has(key)) destination.searchParams.set(key, value);
        });
        link.href = destination.toString();
      } catch (_error) {
        // Keep the original link when it cannot be parsed.
      }
    });
  };

  const fillConfetti = (container) => {
    if (!container || container.children.length) return;
    for (let index = 0; index < 52; index += 1) {
      const piece = document.createElement('span');
      const round = index % 6 === 0;
      const width = 6 + Math.round(Math.random() * 4);
      piece.className = `cls-confetti__piece${round ? ' is-round' : ''}`;
      piece.style.cssText = [
        `left: ${(Math.random() * 100).toFixed(2)}%`,
        `width: ${width}px`,
        `height: ${round ? width : width + Math.round(Math.random() * 8)}px`,
        `background: ${CONFETTI_COLORS[index % CONFETTI_COLORS.length]}`,
        `animation-delay: ${(Math.random() * 0.9).toFixed(2)}s`,
        `animation-duration: ${(2.6 + Math.random() * 1.8).toFixed(2)}s`,
        `--drift: ${Math.round(Math.random() * 80 - 40)}px`,
        `--spin: ${Math.round(540 + Math.random() * 600)}deg`,
      ].join('; ');
      container.append(piece);
    }
  };

  // Restart the confetti every time the offer step opens.
  const playConfetti = (step) => {
    const container = step.querySelector('[data-story-confetti]');
    if (!container) return;
    container.replaceChildren();
    fillConfetti(container);
  };

  const preload = (step) => {
    step?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  };

  // One carousel per step (before/after, reviews), each with its own dots.
  const initCarousel = (step) => {
    const carousel = step.querySelector('[data-story-carousel]');
    const dots = [...step.querySelectorAll('[data-story-dot]')];
    if (!carousel || !dots.length) return;

    const setActive = (active) => dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    carousel.addEventListener('scroll', () => {
      setActive(Math.min(dots.length - 1, Math.round(carousel.scrollLeft / (carousel.clientWidth || 1))));
    }, { passive: true });

    // Mouse drag: the cards follow the pointer, then glide to the nearest card (a short flick is enough to go on).
    let drag = null;
    let restoreSnap = 0;
    const pageWidth = () => {
      const cards = carousel.children;
      return (cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : carousel.clientWidth) || 1;
    };
    const glideTo = (page) => {
      const last = carousel.children.length - 1;
      const target = Math.min(carousel.scrollWidth - carousel.clientWidth, Math.max(0, Math.min(last, page)) * pageWidth());
      const start = carousel.scrollLeft;
      const startTime = performance.now();
      const duration = Math.min(420, 180 + Math.abs(target - start) * 0.6);
      const frame = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        carousel.scrollLeft = start + (target - start) * (1 - Math.pow(1 - progress, 3));
        if (progress < 1 && !drag) restoreSnap = requestAnimationFrame(frame);
        else if (!drag) carousel.classList.remove('is-gliding');
      };
      cancelAnimationFrame(restoreSnap);
      carousel.classList.add('is-gliding');
      restoreSnap = requestAnimationFrame(frame);
    };
    carousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimationFrame(restoreSnap);
      drag = { x: event.clientX, left: carousel.scrollLeft, page: Math.round(carousel.scrollLeft / pageWidth()), time: performance.now(), lastX: event.clientX, lastTime: performance.now(), velocity: 0 };
      carousel.classList.add('is-dragging', 'is-gliding');
      event.preventDefault();
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const now = performance.now();
      drag.velocity = (event.clientX - drag.lastX) / Math.max(1, now - drag.lastTime);
      drag.lastX = event.clientX;
      drag.lastTime = now;
      carousel.scrollLeft = drag.left - (event.clientX - drag.x);
    });
    window.addEventListener('pointerup', (event) => {
      if (!drag) return;
      const moved = event.clientX - drag.x;
      const flick = Math.abs(drag.velocity) > 0.35 || Math.abs(moved) > pageWidth() * 0.2;
      const page = flick ? drag.page - Math.sign(moved) : Math.round(carousel.scrollLeft / pageWidth());
      drag = null;
      carousel.classList.remove('is-dragging');
      glideTo(page);
    });
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => glideTo(index));
    });
    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') glideTo(Math.round(carousel.scrollLeft / pageWidth()) + 1);
      if (event.key === 'ArrowLeft') glideTo(Math.round(carousel.scrollLeft / pageWidth()) - 1);
    });
  };

  const initStory = (story) => {
    if (story.dataset.initialized === 'true') return;
    story.dataset.initialized = 'true';

    const steps = [...story.querySelectorAll('[data-story-step]')];
    const segments = [...story.querySelectorAll('.cls-progress__seg')];
    const back = story.querySelector('[data-story-back]');
    const skip = story.querySelector('[data-story-skip]');
    const count = story.querySelector('[data-story-count]');
    const pad = (number) => String(number).padStart(2, '0');
    let current = Math.max(0, steps.findIndex((step) => step.classList.contains('is-active')));

    const updateChrome = () => {
      segments.forEach((segment, index) => segment.classList.toggle('is-done', index <= current));
      if (count) count.textContent = `${pad(current + 1)} / ${pad(steps.length)}`;
      if (back) back.hidden = current === 0;
      if (skip) skip.hidden = current === steps.length - 1;
    };

    const goTo = (target, completed) => {
      if (target < 0 || target >= steps.length || target === current) return;
      const leaving = steps[current];
      const next = steps[target];
      if (completed) track(`story_step_${current + 1}_complete`);
      leaving.classList.add('is-leaving');
      leaving.classList.remove('is-active');
      window.setTimeout(() => leaving.classList.remove('is-leaving'), 380);
      next.classList.add('is-active');
      next.scrollTop = 0;
      current = target;
      preload(steps[current + 1]);
      if (next.classList.contains('cls-step--offer')) playConfetti(next);
      next.querySelector('.cls-cap')?.focus({ preventScroll: true });
      updateChrome();
    };

    story.querySelectorAll('[data-story-next]').forEach((button) => button.addEventListener('click', () => goTo(current + 1, true)));
    back?.addEventListener('click', () => goTo(current - 1, false));
    skip?.addEventListener('click', () => {
      track('story_offer_skip');
      goTo(steps.length - 1, false);
    });
    story.querySelectorAll('[data-story-cta]').forEach((link) => link.addEventListener('click', () => track('story_offer_click')));

    // In the theme editor, selecting a step block shows that step.
    document.addEventListener('shopify:block:select', (event) => {
      const index = steps.indexOf(event.target.closest('[data-story-step]'));
      if (index !== -1) goTo(index, false);
    });

    passTrackingParameters(story);
    steps.forEach(initCarousel);
    preload(steps[current + 1]);
    if (steps[current]?.classList.contains('cls-step--offer')) playConfetti(steps[current]);
    updateChrome();
  };

  const initAll = () => document.querySelectorAll('[data-story]').forEach(initStory);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
